import { useEffect, useState } from 'react'

export interface ProjectLineageMeta {
  accountManager: string | null
  clientName: string | null
  projectManager: string | null
  loading: boolean
  error: string | null
}

interface LineageBundle {
  clientByProjectId: Map<number, string>
  amPmByProjectId: Map<
    number,
    { accountManager: string; projectManager: string }
  >
}

let bundlePromise: Promise<LineageBundle> | null = null

function loadLineageBundle(): Promise<LineageBundle> {
  if (bundlePromise) return bundlePromise

  bundlePromise = (async () => {
    const [mappingRaw, manifestRes] = await Promise.all([
      fetch(
        '/ts_project_json/client_project/client_projects_mapping_info.json',
      ).then((r) => (r.ok ? r.json() : [])),
      fetch('/jsonbyAM/am_manifest.json').then((r) =>
        r.ok ? r.json() : { account_managers: [] },
      ),
    ])

    const mappingRows: unknown[] = Array.isArray(mappingRaw)
      ? mappingRaw
      : []
    const clientByProjectId = new Map<number, string>()
    for (const row of mappingRows) {
      const o = row as Record<string, unknown>
      const pid = Number(o?.project_id)
      const cn = String(o?.client_name ?? '').trim()
      if (Number.isFinite(pid) && cn) clientByProjectId.set(pid, cn)
    }

    const ams = (
      (manifestRes as { account_managers?: { filename: string }[] })
        .account_managers || []
    ).filter((a) => a?.filename)

    const fileArrays = await Promise.all(
      ams.map((am) =>
        fetch(`/jsonbyAM/${am.filename}`).then((r) =>
          r.ok ? r.json() : [],
        ),
      ),
    )

    const amPmByProjectId = new Map<
      number,
      { accountManager: string; projectManager: string }
    >()

    for (const arr of fileArrays) {
      if (!Array.isArray(arr)) continue
      for (const row of arr) {
        const o = row as Record<string, unknown>
        const pid = Number(o?.project_id)
        if (!Number.isFinite(pid) || amPmByProjectId.has(pid)) continue
        const accountManager = String(o?.account_manager ?? '').trim()
        const projectManager = String(o?.project_manager ?? '').trim()
        if (accountManager || projectManager)
          amPmByProjectId.set(pid, { accountManager, projectManager })
      }
    }

    return { clientByProjectId, amPmByProjectId }
  })().catch((err: Error) => {
    bundlePromise = null
    throw err
  })

  return bundlePromise
}

const emptyMeta: Omit<ProjectLineageMeta, 'loading' | 'error'> = {
  accountManager: null,
  clientName: null,
  projectManager: null,
}

/**
 * Client name from project–client mapping; AM / PM from jsonbyAM timesheet rows
 * (first AM file in manifest order that contains the project).
 */
export function useProjectLineageMeta(
  projectId: number | null,
): ProjectLineageMeta {
  const [state, setState] = useState<ProjectLineageMeta>({
    ...emptyMeta,
    loading: projectId != null && Number.isFinite(projectId),
    error: null,
  })

  useEffect(() => {
    if (projectId == null || !Number.isFinite(projectId)) {
      setState({ ...emptyMeta, loading: false, error: null })
      return
    }

    let cancelled = false
    setState((s) => ({ ...s, loading: true, error: null }))

    loadLineageBundle()
      .then((b) => {
        if (cancelled) return
        const pair = b.amPmByProjectId.get(projectId)
        setState({
          accountManager: pair?.accountManager || null,
          clientName: b.clientByProjectId.get(projectId) || null,
          projectManager: pair?.projectManager || null,
          loading: false,
          error: null,
        })
      })
      .catch((err: Error) => {
        if (!cancelled)
          setState({
            ...emptyMeta,
            loading: false,
            error: err.message || 'Failed to load project lineage',
          })
      })

    return () => {
      cancelled = true
    }
  }, [projectId])

  return state
}
